import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Table } from 'react-bootstrap';
import axios from 'axios';

const RATE_COLUMNS = [
  { key: 'week_rate', label: 'Week' },
  { key: 'one_day_rate', label: '1 Day' },
  { key: 'two_day_rate', label: '2 Days' },
  { key: 'three_day_rate', label: '3 Days' },
  { key: 'four_day_rate', label: '4 Days' },
  { key: 'day_rate', label: 'Day' },
  { key: 'month_rate', label: 'Month' },
];

function ApplyRateModal({
  show,
  onClose,
  items,
  onApplied,
  apiPath,
  categoryName,
  hireCategories = [],
  onRefetchItems,
}) {
  const [hireRates, setHireRates] = useState([]);
  const [selectedRateId, setSelectedRateId] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [applySuccess, setApplySuccess] = useState('');
  const dragRef = useRef({ active: false, mode: null });

  useEffect(() => {
    if (show) {
      setSelectedRateId('');
      setSelectedIds(new Set());
      setError('');
      fetchHireRates();
    }
  }, [show]);

  const fetchHireRates = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/hire-rates', {
        params: { page: 1, limit: 1000 },
      });
      setHireRates(response.data.hireRates || []);
      if (response.data.hireRates?.length > 0) {
        setSelectedRateId(response.data.hireRates[0].id);
      }
    } catch (err) {
      setError('Failed to load hire rates');
      setHireRates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAll = (checked) => {
    if (checked) {
      setSelectedIds(new Set(items.map((item) => item.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleToggleRow = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleDocumentMouseUp = () => {
    dragRef.current = { active: false, mode: null };
    document.removeEventListener('mouseup', handleDocumentMouseUp);
  };

  const handleCheckboxMouseDown = (e, itemId) => {
    e.preventDefault();
    const mode = !selectedIds.has(itemId);
    handleToggleRow(itemId, mode);
    dragRef.current = { active: true, mode };
    document.addEventListener('mouseup', handleDocumentMouseUp);
  };

  const handleRowMouseEnter = (itemId) => {
    if (dragRef.current.active && dragRef.current.mode !== null) {
      handleToggleRow(itemId, dragRef.current.mode);
    }
  };

  const handleApply = async () => {
    if (!selectedRateId) {
      setError('Please select a hire rate');
      return;
    }
    const rate = hireRates.find((r) => r.id === parseInt(selectedRateId, 10));
    if (!rate) {
      setError('Invalid hire rate selected');
      return;
    }
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      setError('Please select at least one item');
      return;
    }

    setError('');
    setApplying(true);
    try {
      const ratePayload = {
        week_rate: rate.week_rate ?? rate.weekly_rate,
        one_day_rate: rate.one_day_rate,
        two_day_rate: rate.two_day_rate,
        three_day_rate: rate.three_day_rate,
        four_day_rate: rate.four_day_rate,
        part_day_rate: rate.part_day_rate,
        weekend_rate: rate.weekend_rate,
        hour_rate: rate.hour_rate,
        day_rate: rate.day_rate ?? rate.daily_rate,
        month_rate: rate.month_rate,
        sc_rate: rate.sc_rate,
        unit_rate: rate.unit_rate,
        switch_week_rate: rate.switch_week_rate,
        switch_after_weeks: rate.switch_after_weeks,
        deposit: rate.deposit,
      };

      for (const id of ids) {
        await axios.put(`${apiPath}/${id}`, ratePayload);
      }

      onApplied?.(ids.length);
      setSelectedIds(new Set());
      setApplySuccess(`Applied to ${ids.length} item(s)`);
      setError('');
      setTimeout(() => setApplySuccess(''), 3000);

      // Refresh modal items with updated rates
      const categoryCode = items[0]?.cat;
      if (onRefetchItems && categoryCode) {
        await onRefetchItems(categoryCode);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to apply rates');
    } finally {
      setApplying(false);
    }
  };

  const formatRate = (value) => {
    if (value == null || value === '') return '';
    const n = parseFloat(value);
    return isNaN(n) ? '' : `£${n.toFixed(2)}`;
  };

  const getCurrentRateName = (categoryCode) => {
    const category = hireCategories.find((c) => c.code === categoryCode);
    if (!category?.hire_rate_id) return '';
    const rate = hireRates.find((r) => r.id === category.hire_rate_id);
    return rate?.name ?? '';
  };

  const allChecked = items.length > 0 && selectedIds.size === items.length;
  const someChecked = selectedIds.size > 0;
  const selectAllRef = useRef(null);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someChecked && !allChecked;
    }
  }, [someChecked, allChecked]);

  return (
    <Modal show={show} onHide={onClose} size="xl" centered style={{ zIndex: 1060 }}>
      <Modal.Header closeButton>
        <Modal.Title>Apply Hire Rate</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {categoryName && (
          <p className="text-muted mb-2">
            Showing items in category: <strong>{categoryName}</strong>
          </p>
        )}
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3 w-100">
          <Form.Group
            className="mb-0 d-flex align-items-center flex-grow-1"
            style={{ minWidth: 0 }}
          >
            <Form.Label className="me-2 mb-0 flex-shrink-0">Select hire rate</Form.Label>
            <Form.Select
              value={selectedRateId}
              onChange={(e) => setSelectedRateId(e.target.value)}
              className="flex-grow-1"
              style={{ minWidth: '120px' }}
            >
              <option value="">Choose a hire rate...</option>
              {hireRates.map((rate) => (
                <option key={rate.id} value={rate.id}>
                  {rate.name}
                </option>
              ))}
            </Form.Select>
            {applySuccess && <span className="text-success ms-2">{applySuccess}</span>}
          </Form.Group>
          <Button
            variant="primary"
            onClick={handleApply}
            disabled={applying || items.length === 0 || !selectedRateId}
            className="ms-auto"
          >
            {applying ? 'Applying...' : 'OK'}
          </Button>
        </div>

        {error && (
          <div className="alert alert-danger py-2">
            {error}
            <button
              type="button"
              className="btn-close btn-close-sm"
              onClick={() => setError('')}
              aria-label="Close"
              style={{ float: 'right' }}
            />
          </div>
        )}

        {loading ? (
          <div className="text-center p-4">Loading hire rates...</div>
        ) : items.length === 0 ? (
          <div className="text-center p-4 text-muted">
            {categoryName
              ? 'No items found in this category.'
              : 'Select a category from the sidebar to see items.'}
          </div>
        ) : (
          <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <Table striped bordered hover size="sm">
              <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={allChecked}
                      ref={selectAllRef}
                      onChange={(e) => handleToggleAll(e.target.checked)}
                    />
                  </th>
                  <th>Stock No</th>
                  <th>Name</th>
                  <th>Current Rate</th>
                  {RATE_COLUMNS.map((col) => (
                    <th key={col.key}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} onMouseEnter={() => handleRowMouseEnter(item.id)}>
                    <td
                      onMouseDown={(e) => handleCheckboxMouseDown(e, item.id)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedIds.has(item.id)}
                        readOnly
                        tabIndex={-1}
                      />
                    </td>
                    <td>{item.stock_no}</td>
                    <td>{item.name}</td>
                    <td>{getCurrentRateName(item.cat) || <em className="text-muted">—</em>}</td>
                    {RATE_COLUMNS.map((col) => (
                      <td key={col.key}>{formatRate(item[col.key])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ApplyRateModal;
